/**
 * Storage Utilities
 *
 * Shared localStorage helpers for session management.
 * Used by both Auth0 and OIDC session hooks.
 */

// ============================================================================
// TAB IDENTIFIER
// ============================================================================

// Tab identifier (singleton per tab)
let tabId = null;

/**
 * Generate or get unique tab identifier
 * @returns {string} Unique tab ID
 */
export const getTabId = () => {
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  return tabId;
};

/**
 * Reset tab ID (call on logout for clean re-auth)
 */
export const resetTabId = () => {
  tabId = null;
};

// ============================================================================
// TIMESTAMP HELPERS
// ============================================================================

/**
 * Read timestamp from localStorage
 * @param {string} key - Storage key
 * @param {number} fallback - Default value if key not found or invalid
 * @returns {number} Timestamp in milliseconds
 */
export const readTimestamp = (key, fallback = 0) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  } catch (error) {
    // localStorage unavailable
  }
  return fallback;
};

/**
 * Write timestamp to localStorage
 * @param {string} key - Storage key
 * @param {number} timestamp - Timestamp in milliseconds
 */
export const writeTimestamp = (key, timestamp) => {
  try {
    localStorage.setItem(key, timestamp.toString());
  } catch (error) {
    // localStorage unavailable
  }
};

/**
 * Remove key from localStorage
 * @param {string} key - Storage key
 */
export const removeStorageKey = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // localStorage unavailable
  }
};

/**
 * Clear multiple keys from localStorage
 * @param {string[]} keys - Array of storage keys to remove
 */
export const clearStorageKeys = (keys) => {
  keys.forEach((key) => removeStorageKey(key));
};

/**
 * Clear keys matching a pattern from localStorage
 * @param {string} pattern - String pattern to match (uses includes())
 */
export const clearStorageByPattern = (pattern) => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.includes(pattern)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    return keysToRemove.length;
  } catch (error) {
    return 0;
  }
};

// ============================================================================
// JSON STORAGE HELPERS
// ============================================================================

/**
 * Store JSON data in localStorage
 * @param {string} key - Storage key
 * @param {*} data - Data to store (will be JSON stringified)
 * @returns {boolean} True if successful
 */
export const storeJSON = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('[Storage] Failed to store JSON:', error);
    return false;
  }
};

/**
 * Read JSON data from localStorage
 * @param {string} key - Storage key
 * @returns {*} Parsed data or null if not found/invalid
 */
export const readJSON = (key) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.error('[Storage] Failed to read JSON:', error);
    return null;
  }
};

/**
 * Check if a key exists in localStorage
 * @param {string} key - Storage key
 * @returns {boolean} True if key exists
 */
export const hasStorageKey = (key) => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    return false;
  }
};
